import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Form, Table, Button, Pagination, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Info, Diamond as DiamondIcon } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import MultiRangeSlider from '../components/MultiRangeSlider';
import round_png from '../assets/round_png2.png'
import oval_png from '../assets/oval_png.png'
import emerald_png from '../assets/emerald_png.png'
import heart_png from '../assets/hart_png.png'
import princess_png from '../assets/princess_png.png'
import pear_png from '../assets/pear_png.png'
import marquise_png from '../assets/marquise_png.png'
import radiant_png from '../assets/radiant.png'
import cushion_png from '../assets/cushion_png.png'
import { useTranslation } from 'react-i18next';

const Diamonds = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Custom styling for the filters
  const filterStyle = `
    .filter-pill {
      border: 1px solid #dee2e6;
      border-radius: 20px;
      padding: 6px 12px;
      font-size: 13px;
      cursor: pointer;
      transition: all 0.2s ease;
      background: white;
      color: #495057;
      user-select: none;
      text-align: center;
      min-width: 50px;
      display: inline-block;
    }
    .filter-pill:hover {
      border-color: #007bff;
      color: #007bff;
    }
    .filter-pill.active {
      background-color: #007bff;
      border-color: #007bff;
      color: white;
      box-shadow: 0 4px 6px rgba(0, 123, 255, 0.2);
    }
    .filter-label {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: #6c757d;
      margin-bottom: 12px;
      display: block;
    }
    .filter-group-container {
      background: #f8f9fa;
      padding: 20px;
      border-radius: 12px;
      height: 100%;
    }
  `;

  const [diamonds, setDiamonds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [caratDisplay, setCaratDisplay] = useState({ min: 0.25, max: 20 });

  const [filters, setFilters] = useState({
    shapes: [],
    source: 'lab grown',
    minCarat: 0.25,
    maxCarat: 20,
    colors: [],
    clarities: [],
    fancyColors: [],
    fancy_color_inte: []
  });

  const [fancyOptions, setFancyOptions] = useState({ colors: [], intensities: [] });

  const shapes = [
    { name: 'Round', icon: round_png },
    { name: 'Oval', icon: oval_png },
    { name: 'Pear', icon: pear_png },
    { name: 'Marquise', icon: marquise_png },
    { name: 'Princess', icon: princess_png },
    { name: 'Emerald', icon: emerald_png },
    { name: 'Radiant', icon: radiant_png },
    { name: 'Cushion', icon: cushion_png },
    { name: 'Heart', icon: heart_png }
  ];

  const colorOptions = ['D', 'E', 'F', 'G', 'H', 'I', 'J', 'K'];
  const clarityOptions = ['FL', 'IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2'];

  useEffect(() => {
    const fetchFancyOptions = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/diamonds/getFancyFilterOptions`);
        if (response.data && response.data.success) {
          setFancyOptions(response.data.data);
        }
      } catch (err) {
        console.error('Error fetching fancy options:', err);
      }
    };
    fetchFancyOptions();
  }, []);

  const fetchDiamonds = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        pageSize,
        source: filters.source,
        minCarat: filters.minCarat,
        maxCarat: filters.maxCarat,
      });
      
      if (filters.shapes.length > 0) {
        params.append('shapes', filters.shapes.map(s => s.toUpperCase()).join(','));
      }

      if (filters.colors.length > 0) {
        params.append('colors', filters.colors.join(','));
      }

      if (filters.clarities.length > 0) {
        params.append('clarities', filters.clarities.join(','));
      }

      if (filters.fancyColors.length > 0) {
        params.append('fancyColor', filters.fancyColors.join(','));
      }

      if (filters.fancy_color_inte.length > 0) {
        params.append('fancy_color_inte', filters.fancy_color_inte.join(','));
      }

      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/diamonds/getDiamonds?${params.toString()}`);
      if (response.data && response.data.success) {
        setDiamonds(response.data.data);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error('Error fetching diamonds:', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    fetchDiamonds();
  }, [fetchDiamonds]);

  const toggleShape = (shape) => {
    setFilters(prev => {
      const shapes = prev.shapes.includes(shape)
        ? prev.shapes.filter(s => s !== shape)
        : [...prev.shapes, shape];
      return { ...prev, shapes };
    });
    setPage(1);
  };

  const handleCaratChange = useCallback(({ min, max }) => {
    setCaratDisplay({ min, max });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => {
        if (prev.minCarat === caratDisplay.min && prev.maxCarat === caratDisplay.max) return prev;
        return { ...prev, minCarat: caratDisplay.min, maxCarat: caratDisplay.max };
      });
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [caratDisplay]);

  const toggleFilter = (type, value) => {
    setFilters(prev => {
      const current = prev[type];
      const updated = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];
      return { ...prev, [type]: updated };
    });
    setPage(1);
  };

  return (
    <section className="pt-5 pb-5">
      <style>{filterStyle}</style>
      <Container>
        {/* Filters Area */}
        <div className="bg-white p-4 rounded-4 shadow-sm mb-5 border" data-aos="fade-right">
          <Row className="mb-4">
            <Col lg={12}>
              <Form.Label className="small fw-bold text-uppercase mb-3 sunflower text-dark" style={{ letterSpacing: '1px' }}>{t('diamonds.filters.chooseShape')}</Form.Label>
              <div className="d-flex flex-wrap gap-2 justify-content-center">
                {shapes.map(s => (
                  <div 
                    key={s.name} 
                    className={`filter-shape-btn ${filters.shapes.includes(s.name) ? 'active' : ''}`}
                    onClick={() => toggleShape(s.name)}
                  >
                    <img src={s.icon} alt={s.name} onError={(e) => { e.target.src = 'https://vanbruun.com/images/shapes/round.svg'; }} />
                    <span className="small" style={{ color: '#000' }}>{s.name}</span>
                  </div>
                ))}
              </div>
            </Col>
          </Row>

          <Row className="g-4">            
            <Col lg={6}>
              <Form.Label className="small fw-bold text-uppercase mb-3 sunflower text-dark" style={{ letterSpacing: '1px' }}>{t('diamonds.filters.caratRange')}</Form.Label>
              <div className="range-slider-container px-2 pt-3">
                <MultiRangeSlider
                  min={0.25}
                  max={25}
                  onChange={handleCaratChange}
                />
                <div className="d-flex justify-content-between mt-1 px-1">
                  <div className="small fw-bold text-dark">{caratDisplay.min.toFixed(2)} ct</div>
                  <div className="small fw-bold text-dark">{caratDisplay.max.toFixed(2)} ct</div>
                </div>
              </div>
            </Col>

            <Col lg={3}>
              <div className="filter-group-container">
                <Form.Label className="small fw-bold text-uppercase mb-3 sunflower text-dark" style={{ letterSpacing: '1px' }}>{t('diamonds.filters.color')}</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {colorOptions.map(c => (
                    <div 
                      key={c}
                      className={`filter-pill ${filters.colors.includes(c) ? 'active' : ''}`}
                      onClick={() => toggleFilter('colors', c)}
                    >
                      {c}
                    </div>
                  ))}
                </div>
              </div>
            </Col>
            <Col lg={3}>
              <div className="filter-group-container">
                <Form.Label className="small fw-bold text-uppercase mb-3 sunflower text-dark" style={{ letterSpacing: '1px' }}>{t('diamonds.filters.clarity')}</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {clarityOptions.map(c => (
                    <div 
                      key={c}
                      className={`filter-pill ${filters.clarities.includes(c) ? 'active' : ''}`}
                      onClick={() => toggleFilter('clarities', c)}
                    >
                      {c}
                    </div>
                  ))}
                </div>
              </div>
            </Col>
            
            <Col lg={12}>
                <Row className="g-4">
                  <Col lg={6}>
                    <div className="filter-group-container">
                      <Form.Label className="small fw-bold text-uppercase mb-3 sunflower text-dark" style={{ letterSpacing: '1px' }}>{t('diamonds.filters.fancyColor')}</Form.Label>
                      <Form.Select 
                        value={filters.fancyColors[0] || ''} 
                        onChange={(e) => {
                          const val = e.target.value;
                          setFilters(prev => ({ ...prev, fancyColors: val ? [val] : [] }));
                          setPage(1);
                        }}
                      >
                        <option value="">{t('diamonds.filters.allFancyColors')}</option>
                        {fancyOptions.colors.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </Form.Select>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="filter-group-container">
                      <Form.Label className="small fw-bold text-uppercase mb-3 sunflower text-dark" style={{ letterSpacing: '1px' }}>{t('diamonds.filters.fancyIntensity')}</Form.Label>
                      <Form.Select 
                        value={filters.fancy_color_inte[0] || ''} 
                        onChange={(e) => {
                          const val = e.target.value;
                          setFilters(prev => ({ ...prev, fancy_color_inte: val ? [val] : [] }));
                          setPage(1);
                        }}
                      >
                        <option value="">{t('diamonds.filters.allIntensities')}</option>
                        {fancyOptions.intensities.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </Form.Select>
                    </div>
                  </Col>
                </Row>
            </Col>

          </Row>
        </div>

        {/* Results Area */}
        <div className="diamond-list-table" data-aos="fade-left" data-aos-delay="200">
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                <th>{t('diamonds.table.shape')}</th>
                <th>{t('diamonds.table.carat')}</th>
                <th>{t('diamonds.table.color')}</th>
                <th>{t('diamonds.table.clarity')}</th>
                <th>{t('diamonds.table.fluorescence')}</th>
                <th>{t('diamonds.table.fancyColor')}</th>
                <th>{t('diamonds.table.polish')}</th>
                <th>{t('diamonds.table.price')}</th>
                <th>{t('diamonds.table.action')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                  </td>
                </tr>
              ) : diamonds.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-5 text-muted">{t('diamonds.table.noResults')}</td>
                </tr>
              ) : (
                diamonds.map(d => (
                  <tr key={d.diamond_id} className="diamond-list-row" onClick={() => navigate(`/diamond/${d.diamond_id}`)}>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                         <DiamondIcon size={16} className="text-muted" />
                         <span className="fw-medium">{d.cut}</span>
                      </div>
                    </td>
                    <td>{d.carat} ct</td>
                    <td>{d.color}</td>
                    <td>{d.clarity}</td>
                    <td>{d.fluorescence || '-'}</td>
                    <td>{d.fancy_color && d.fancy_color_inte ? `${d.fancy_color} ${d.fancy_color_inte || ''}`.trim() : '-'}</td>
                    <td>{d.polish || '-'}</td>
                    <td className="fw-bold text-dark">€ {d.price || '--'}</td>
                    <td>
                      <Button 
                        variant="link" 
                        size="sm" 
                        className="p-0 text-decoration-none d-flex align-items-center gap-1 sunflower"
                        style={{ color: 'var(--color-accent)' }}
                      >
                        <Info size={16} /> {t('common.details')}
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {/* Pagination */}
        {total > pageSize && (
          <div className="d-flex flex-column align-items-center mt-5">
            <div className="d-flex align-items-center gap-3 mb-3">
              <div className="text-muted small sunflower">
                {t('diamonds.pagination.page')} <strong>{page}</strong> {t('diamonds.pagination.of')} <strong>{Math.ceil(total / pageSize)}</strong> ({t('diamonds.pagination.total')} {total} {t('diamonds.pagination.diamonds')})
              </div>
              <Form.Select 
                size="sm" 
                className="w-auto ms-2"
                value={pageSize}
                onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                }}
              >
                <option value="10">10 {t('diamonds.pagination.perPage')}</option>
                <option value="25">25 {t('diamonds.pagination.perPage')}</option>
                <option value="50">50 {t('diamonds.pagination.perPage')}</option>
                <option value="100">100 {t('diamonds.pagination.perPage')}</option>
              </Form.Select>
            </div>
            <Pagination className="sunflower shadow-sm">
              <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
              <Pagination.Prev onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} />
              
              {[...Array(Math.ceil(total / pageSize))].map((_, i) => (
                <Pagination.Item 
                  key={i + 1} 
                  active={i + 1 === page} 
                  onClick={() => setPage(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}

              <Pagination.Next onClick={() => setPage(p => Math.min(Math.ceil(total / pageSize), p + 1))} disabled={page === Math.ceil(total / pageSize)} />
              <Pagination.Last onClick={() => setPage(Math.ceil(total / pageSize))} disabled={page === Math.ceil(total / pageSize)} />
            </Pagination>
          </div>
        )}
        {/* Custom Inquiry Section */}
        <div className="mt-5 p-5 rounded-4 text-center glass-panel shadow-sm border" style={{ backgroundColor: '', borderColor: 'var(--color-accent)' }}>
          <h3 className="fw-bold mb-3" style={{ fontFamily: 'var(--font-serif)', color: 'var(--color-accent-dark)' }}>{t('diamonds.inquiry.title')}</h3>
          <p className="mb-4 sunflower" style={{ color: 'var(--color-accent-dark)', opacity: 0.8 }}>
            {t('diamonds.inquiry.description')}
          </p>
          <a 
            className="btn btn-primary px-5 py-3 fw-bold text-uppercase rounded-4 d-inline-flex align-items-center justify-content-center gap-2"
            style={{ backgroundColor: 'var(--color-accent)', borderColor: 'var(--color-accent)', color: 'white', textDecoration: 'none' }}
            href="https://api.whatsapp.com/send/?phone=+4915202693620&text=Hi%20I'm%20looking%20for%20a%20custom%20diamond&type=phone_number&app_absent=0"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaWhatsapp size={20} /> {t('diamonds.inquiry.cta')}
          </a>
        </div>
      </Container>
    </section>
  );
};

export default Diamonds;
